import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { SectionProps } from '../SectionProps';
import { Controller, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { DownloadLink } from '@ofqual/portal-components/components/DownloadLink';
import { TextField } from '@ofqual/portal-components/components/TextField';
import { FileInput } from '@ofqual/portal-components/components/FileInput';
import { fieldLabels } from '../fieldLabels';
import {
  SocItemDetailsAo,
  SocOrganisationStability,
} from '../../../domain/SocItemDetailsAo';
import { useAuth } from '@ofqual/portal-core/core/hooks';
import { aoSocActiveSubmissionsSelector, authSelector, itemYearSelector } from '../../../redux/selectors';
import { socService } from '../../../services';
import { useSelector } from 'react-redux';
import { Error } from "../../common/Error";
import { MAX_TOTAL_SIZE_MB, validateFiles } from "../../utils/fileSizeLimit"

export const QuestionOneSection: React.FC<SectionProps<SocItemDetailsAo>> = (
  sectionProps: SectionProps<SocItemDetailsAo>,
) => {
  const { control, disabled, errors, clearErrors, trigger, formState: { isSubmitted } } = sectionProps;
  const { token } = useAuth(authSelector);
  const { fullAccountsUpload } = useWatch<SocItemDetailsAo>({
    control,
    name: ['fullAccountsUpload'],
  });

  const activeFiles = useSelector(aoSocActiveSubmissionsSelector);
  const year = useSelector(itemYearSelector) ?? "2024"
  const submissionFile = activeFiles?.find((item) => item.submissionType === "AccountStatement" && item.active)
  const fileRequired = submissionFile === undefined && fullAccountsUpload === SocOrganisationStability.fullAccountsUploadYes
  const accountsDescriptionRequired = fullAccountsUpload === SocOrganisationStability.fullAccountsUploadNoCircumstances

  useEffect(() => {

    if (isSubmitted) {
      trigger('fullAccountsUploadDescription')
      trigger('accountStatementAttachment')
    }

  }, [trigger, fullAccountsUpload, isSubmitted]);

  if (fullAccountsUpload !== SocOrganisationStability.fullAccountsUploadNoCircumstances && isSubmitted && errors['fullAccountsUploadDescription']) {
    clearErrors('fullAccountsUploadDescription')
  }

  if (fullAccountsUpload !== SocOrganisationStability.fullAccountsUploadYes && isSubmitted && errors['accountStatementAttachment']) {
    clearErrors('accountStatementAttachment')
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6">
          Current performance
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="fullAccountsUpload"
          rules={{ validate: (value) => value !== null }}
          render={(props) => (
            <FormControl component="fieldset" required disabled={disabled}>
              <RadioGroup
                aria-labelledby="fullAccountsUpload-label"
                name="fullAccountsUpload"
                data-test="radiogroup-fullAccountsUpload"
                onChange={(e) => {
                  props.onChange(Number(e.target.value));
                }}
              >
                <>
                  <br />
                </>
                <Typography variant="body1">
                  {fieldLabels.fullAccountsUpload}
                </Typography>
                <FormControlLabel
                  value={SocOrganisationStability.fullAccountsUploadYes}
                  label={fieldLabels.fullAccountsUploadYes}
                  control={<Radio />}
                  data-test="fullAccountsUploadYes"
                  checked={
                    props.value ===
                    SocOrganisationStability.fullAccountsUploadYes
                  }
                />

                {fullAccountsUpload === SocOrganisationStability.fullAccountsUploadYes ?
                  <div style={{ maxWidth: "100%", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    <Controller
                      control={control}
                      name="accountStatementAttachment"
                      rules={{ required: fileRequired, validate: validateFiles }}
                      as={<FileInput
                        accept=".pdf, .odf, .doc, .docx, .xls, .csv, .ods, .xlsx"
                        dataTest='account-statement-add-attach-button'
                        disabled={disabled}
                      />
                      }
                      onChange={([event]: any) => {
                        const target = event.target as HTMLInputElement;
                        return target.files ? Array.from(target.files) : [];
                      }}
                    />
                    {submissionFile &&
                      <div style={{ marginTop: "5px" }}>
                        <DownloadLink
                          fileName={submissionFile!.fileName}
                          getBlob={() => socService.downloadSocFiles(
                            year,
                            submissionFile!.id,
                            submissionFile!.socId,
                            { token: token })}
                        />
                      </div>
                    }
                  </div>
                  : null}

                {errors['accountStatementAttachment']  && (
                  <div style={{ marginTop: '15px' }}>
                    {errors['accountStatementAttachment'].type === "validate" && (
                      <Error data-test="questionon-file-validation-error">
                        The file must be smaller than {MAX_TOTAL_SIZE_MB}MB
                      </Error>
                    )}
                    {errors['accountStatementAttachment'].type === "required" && (
                      <Error data-test="questionon-file-required-error">
                        Please select a file to attach
                      </Error>
                    )}
                  </div>
                )}

                <FormControlLabel
                  value={SocOrganisationStability.fullAccountsUploadEndJanuary}
                  label={fieldLabels.fullAccountsUploadEndJanuary}
                  control={<Radio />}
                  data-test="fullAccountsUploadEndJanuary"
                  checked={
                    props.value ===
                    SocOrganisationStability.fullAccountsUploadEndJanuary
                  }
                />
                <FormControlLabel
                  value={SocOrganisationStability.fullAccountsUploadEndFebruary}
                  label={fieldLabels.fullAccountsUploadEndFebruary}
                  control={<Radio />}
                  data-test="fullAccountsUploadEndFebruary"
                  checked={
                    props.value ===
                    SocOrganisationStability.fullAccountsUploadEndFebruary
                  }
                />
                <FormControlLabel
                  value={SocOrganisationStability.fullAccountsUploadEndMarch}
                  label={fieldLabels.fullAccountsUploadEndMarch}
                  control={<Radio />}
                  data-test="fullAccountsUploadEndMarch"
                  checked={
                    props.value ===
                    SocOrganisationStability.fullAccountsUploadEndMarch
                  }
                />
                <FormControlLabel
                  value={
                    SocOrganisationStability.fullAccountsUploadNoCircumstances
                  }
                  label={fieldLabels.fullAccountsUploadNoCircumstances}
                  control={<Radio />}
                  data-test="fullAccountsUploadNoCircumstances"
                  checked={
                    props.value ===
                    SocOrganisationStability.fullAccountsUploadNoCircumstances
                  }
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        {errors.fullAccountsUpload && (
          <>
            <br></br>
            <Error data-test="fullAccountsUpload-validation">
              You must select at one option
            </Error>
          </>
        )}

        {fullAccountsUpload === SocOrganisationStability.fullAccountsUploadNoCircumstances &&
          <>
            <Controller
              control={control}
              rules={{ required: accountsDescriptionRequired }}
              name="fullAccountsUploadDescription"
              render={(props) => (
                <>
                  <TextField
                    {...props}
                    label={fieldLabels.FullAccountsUploadDescription}
                    required
                    data-test="fullAccountsUploadDescription-field"
                    multiline
                    rows={8}
                    readOnly={disabled}
                  />
                </>
              )}
            />
          </>
        }
        {errors.fullAccountsUploadDescription && (
          <Error data-test="fullAccountsUploadDescription-validation">
            This field is required
          </Error>
        )}

      </Grid>
    </>
  );
};
